import aa, { InsightsEvent } from 'search-insights';
import { chunk } from 'lodash-es';
import { algoliaSearch } from '@module/Algolia/resources/js/search/AlgoliaSearch';
import { InsightsTypes } from '@module/Algolia/resources/js/insights/types';
import { ping } from '@phoenix/components/Ping';
import { CURRENT_USER_IS_LOGGED, emitter } from '@phoenix/event-bus';

const TRUST_COMMANDER_ANALYTICAL_COOKIES_CATEGORY_ID = '2';
const AVOID_CONSENT_FOR_COOKIES_FEATURE_FLAG = 'avoid_consent_for_cookies';

type TrustCommanderConsentCategory = {
  status?: string;
};

type TrustCommanderConsent = {
  consent?: {
    status?: string;
    categories?: Record<string, TrustCommanderConsentCategory>;
  };
};

type TrustCommanderConsentCallback = (consent: TrustCommanderConsent) => void;
type TrustCommanderWindow = Window & {
  caReady?: [string, TrustCommanderConsentCallback][];
  tC?: {
    cact?: (eventName: string, callback: TrustCommanderConsentCallback) => void;
  };
};
type PingData = {
  data?: {
    isLogged?: boolean;
    customerInfo?: {
      id?: string;
    };
  };
};

type AlgoliaInsightsClickData = {
  eventName: string;
  index: string;
  objectIDs: string[];
  queryID?: string;
  positions?: number[];
  objectData?: InsightsEvent['objectData'];
  value?: InsightsEvent['value'];
  currency?: InsightsEvent['currency'];
};

type AlgoliaInsightsSearchClickData = AlgoliaInsightsClickData & {
  queryID: string;
  positions: number[];
};

type AlgoliaInsightsSearchConversionData = AlgoliaInsightsClickData & {
  queryID: string;
};

let hasUserAcceptedAnalyticalCookies = false;
let authenticatedUserTokenPromise: Promise<void> | null = null;

aa('init', {
  appId: window.Phoenix.config.ALGOLIA.appId,
  apiKey: window.Phoenix.config.ALGOLIA.apiKey,
  useCookie: false,
});

const hasAnalyticalCookieConsent = (consent: TrustCommanderConsent): boolean => {
  if (consent?.consent?.status === 'all-on') {
    return true;
  }

  return consent?.consent?.categories?.[TRUST_COMMANDER_ANALYTICAL_COOKIES_CATEGORY_ID]?.status === 'on';
};

const clearAuthenticatedUserToken = (): void => {
  authenticatedUserTokenPromise = null;
  aa('setAuthenticatedUserToken', undefined);
};

const initializeAuthenticatedUserToken = (): Promise<void> => {
  if (!hasUserAcceptedAnalyticalCookies) {
    clearAuthenticatedUserToken();
    return Promise.resolve();
  }

  const tokenPromise =
    authenticatedUserTokenPromise ??
    ping
      .getData()
      .then((pingData: PingData) => {
        const authenticatedUserToken = pingData?.data?.isLogged === true ? pingData?.data?.customerInfo?.id : undefined;

        aa('setAuthenticatedUserToken', authenticatedUserToken);
      })
      .catch((e: unknown) => {
        console.warn('Error when setting Algolia authenticated user token', e);
        aa('setAuthenticatedUserToken', undefined);
      });

  authenticatedUserTokenPromise = tokenPromise;

  return tokenPromise;
};

const refreshAuthenticatedUserToken = (): void => {
  authenticatedUserTokenPromise = null;

  if (hasUserAcceptedAnalyticalCookies) {
    void initializeAuthenticatedUserToken();
  }
};

const updateCookieConsent = (consent: TrustCommanderConsent): void => {
  hasUserAcceptedAnalyticalCookies = hasAnalyticalCookieConsent(consent);

  aa('init', {
    partial: true,
    useCookie: hasUserAcceptedAnalyticalCookies,
  });

  if (!hasUserAcceptedAnalyticalCookies) {
    clearAuthenticatedUserToken();
    return;
  }

  void initializeAuthenticatedUserToken();
};

const canSkipCookieConsent = (): boolean => {
  return window.Phoenix.featureFlags?.[AVOID_CONSENT_FOR_COOKIES_FEATURE_FLAG] === true;
};

const callTrustCommander = (eventName: string, callback: TrustCommanderConsentCallback): void => {
  const trustCommanderWindow = window as TrustCommanderWindow;
  const trustCommander = trustCommanderWindow.tC;

  if (typeof trustCommander?.cact !== 'function') {
    trustCommanderWindow.caReady = trustCommanderWindow.caReady ?? [];
    trustCommanderWindow.caReady.push([eventName, callback]);
    return;
  }

  trustCommander.cact(eventName, callback);
};

const initializeConsent = (): void => {
  if (canSkipCookieConsent()) {
    updateCookieConsent({
      consent: {
        status: 'all-on',
      },
    });
    return;
  }

  callTrustCommander('consent.get', updateCookieConsent);
  callTrustCommander('consent.onUpdate', updateCookieConsent);
};

initializeConsent();

emitter.on(CURRENT_USER_IS_LOGGED, refreshAuthenticatedUserToken);

export default {
  handleViews: async (ids: string[], index: string, eventName: string) => {
    if (!hasUserAcceptedAnalyticalCookies || ids.length === 0) {
      return;
    }

    const sendViews = async (items: string[]) => {
      try {
        await aa('viewedObjectIDs', {
          objectIDs: items,
          index: algoliaSearch.getFullIndexName(index),
          eventName,
        });
      } catch (e) {
        console.warn('Error when sending data to Algolia analytics', e);
      }
    };

    await authenticatedUserTokenPromise;

    if (!hasUserAcceptedAnalyticalCookies) {
      return;
    }

    if (ids.length <= 20) {
      await sendViews(ids);
    }

    await Promise.all(chunk(ids, 20).map(sendViews));
  },

  handleClick: async (data: AlgoliaInsightsClickData, type: InsightsTypes = 'click') => {
    if (!hasUserAcceptedAnalyticalCookies || !data.objectIDs || !data.index) {
      return;
    }

    data.index = algoliaSearch.getFullIndexName(data.index);

    await authenticatedUserTokenPromise;

    if (!hasUserAcceptedAnalyticalCookies) {
      return;
    }

    try {
      if (type === 'addToCart') {
        if (data.queryID) {
          await aa('addedToCartObjectIDsAfterSearch', data as AlgoliaInsightsSearchConversionData);
          return;
        }

        await aa('addedToCartObjectIDs', data);
        return;
      }

      if (type === 'conversion') {
        if (data.queryID) {
          await aa('convertedObjectIDsAfterSearch', data as AlgoliaInsightsSearchConversionData);
          return;
        }

        await aa('convertedObjectIDs', data);
        return;
      }

      if (data.queryID) {
        await aa('clickedObjectIDsAfterSearch', data as AlgoliaInsightsSearchClickData);
        return;
      }

      await aa('clickedObjectIDs', data);
    } catch (e) {
      console.warn('Error when sending data to Algolia analytics', e);
    }
  },
};
